import React, { CSSProperties, useRef } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { lockDragAndDropHorizontalAxis } from '@/experiences/sales-portal/components/sortable-list/sortable-list-utils.web';
import { measureVisibleElement } from '@experiences/components/virtualization/measure-visible-element.web';
import { Props } from '@/experiences/sales-portal/components/sortable-list/sortable-list-types';
import { SortableListItem } from '@/experiences/sales-portal/components/sortable-list/sortable-list-item.web';
import { useIsFocused } from '@react-navigation/native';
import { useVirtualizer } from '@tanstack/react-virtual';

const DEFAULT_OVERSCAN = 5;
// Positioned container so absolutely-placed virtual rows stack correctly.
const DROPPABLE_VIRTUALIZED_BASE_STYLE: CSSProperties = {
  position: 'relative',
};

const SCROLL_CONTAINER_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};
// Seeds the virtualizer's viewport before its ResizeObserver reports a real
// size, so the first render windows to one screenful instead of mounting every
// row. Width is irrelevant.
const INITIAL_VIEWPORT_RECT = {
  height: typeof window === 'undefined' ? 0 : window.innerHeight,
  width: 0,
};

export const SortableList = <T,>({
  disabled = false,
  dragHandleAccessibilityLabel,
  dragHandleAnchorHeight,
  estimateItemHeight,
  footer,
  getItemId,
  items,
  onReorder,
  overscan = DEFAULT_OVERSCAN,
  renderItemContent,
  rowStyle,
  showHandleOnHover = false,
}: Props<T>) => {
  const isScreenFocused = useIsFocused();
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => estimateItemHeight,
    getScrollElement: () => scrollRef.current,
    initialRect: INITIAL_VIEWPORT_RECT,
    measureElement: measureVisibleElement,
    overscan: Math.max(1, overscan),
  });

  const virtualItems = virtualizer.getVirtualItems();

  const hasHadLayoutRef = useRef(false);
  if (!hasHadLayoutRef.current && (scrollRef.current?.offsetHeight ?? 0) > 0) {
    hasHadLayoutRef.current = true;
  }
  // Fall back to rendering every row only when the scroll container has never
  // had layout (e.g. jsdom, where there's none to measure). Blurred screens
  // render no rows at all.
  const isVirtualized =
    virtualItems.length > 0 || hasHadLayoutRef.current || !isScreenFocused;
  const rows: { index: number; style?: CSSProperties }[] = isVirtualized
    ? virtualItems.map((virtualItem) => ({
        index: virtualItem.index,
        style: {
          left: 0,
          position: 'absolute',
          right: 0,
          top: virtualItem.start,
        },
      }))
    : items.map((_, index) => ({ index }));

  return (
    <div ref={scrollRef} style={SCROLL_CONTAINER_STYLE}>
      <DragDropContext
        onDragEnd={(result) => {
          if (
            !result.destination ||
            result.destination.index === result.source.index
          ) {
            return;
          }
          onReorder(result.source.index, result.destination.index);
        }}
      >
        <Droppable
          droppableId="list"
          isDropDisabled={disabled}
          mode="virtual"
          renderClone={(providedDrag, _snapshot, rubric) => {
            const item = items[rubric.source.index];
            // The dragged item can vanish if the list changes mid-drag; hand the
            // library a valid (empty) draggable node rather than crashing.
            if (!item) {
              return (
                <div
                  ref={providedDrag.innerRef}
                  {...providedDrag.draggableProps}
                />
              );
            }
            const locked = lockDragAndDropHorizontalAxis(providedDrag);
            return (
              <SortableListItem
                content={renderItemContent(item, rubric.source.index)}
                disabled={disabled}
                dragHandleAccessibilityLabel={dragHandleAccessibilityLabel}
                dragHandleAnchorHeight={dragHandleAnchorHeight}
                isDragging
                provided={locked}
                rowStyle={rowStyle}
                showHandleOnHover={showHandleOnHover}
              />
            );
          }}
        >
          {(providedDrop) => (
            // @hello-pangea/dnd requires a div for its droppable props.
            <div
              ref={providedDrop.innerRef}
              role="list"
              style={
                isVirtualized
                  ? {
                      ...DROPPABLE_VIRTUALIZED_BASE_STYLE,
                      height: virtualizer.getTotalSize(),
                    }
                  : undefined
              }
              {...providedDrop.droppableProps}
            >
              {rows.map(({ index, style }) => {
                const item = items[index];
                return (
                  <Draggable
                    draggableId={getItemId(item)}
                    index={index}
                    isDragDisabled={disabled}
                    key={getItemId(item)}
                  >
                    {(providedDrag, snapshot) => (
                      <SortableListItem
                        content={renderItemContent(item, index)}
                        dataIndex={index}
                        disabled={disabled}
                        dragHandleAccessibilityLabel={
                          dragHandleAccessibilityLabel
                        }
                        dragHandleAnchorHeight={dragHandleAnchorHeight}
                        isDragging={snapshot.isDragging}
                        measureRef={virtualizer.measureElement}
                        posInSet={index + 1}
                        provided={lockDragAndDropHorizontalAxis(providedDrag)}
                        rowStyle={rowStyle}
                        setSize={items.length}
                        showHandleOnHover={showHandleOnHover}
                        wrapperStyle={style}
                      />
                    )}
                  </Draggable>
                );
              })}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {footer}
    </div>
  );
};
